import React, { DragEvent } from 'react';
import { GripVertical, Edit3, Trash2, ChevronRight, Ungroup } from 'lucide-react';
import RowActionsMenu, { RowAction } from './RowActionsMenu';
import utilityService from '../../../shared/utils/utilityService';
import { formatDuration, calculateProgress, calculateTimeRemaining, URGENCY_COLORS } from '@thekwallace/backtime-shared';

type ElementState = 'expired' | 'active' | 'upcoming';
type UrgencyLevel = 'imminent' | 'soon' | 'moderate' | 'distant';

interface TimeElement {
  id: string;
  description: string;
  duration: number;
  startTime: number;
  endTime: number;
  countdown?: number;
}

interface TimeElementCardProps {
  element: TimeElement;
  state: ElementState;
  isNextUp?: boolean;
  urgency?: UrgencyLevel;
  showCountdown?: boolean;
  showActualTimes?: boolean;
  formattedStartTime?: string;
  currentTime?: number;
  onEdit?: () => void;
  onDelete?: () => void;
  draggable?: boolean;
  onDragStart?: (e: DragEvent<HTMLDivElement>) => void;
  onDragEnd?: (e: DragEvent<HTMLDivElement>) => void;
  onDragOver?: (e: DragEvent<HTMLDivElement>) => void;
  isDragging?: boolean;
  durationNote?: string;
  offsetInfo?: string;
  // Group variant (collapsed group renders like a normal element with a disclosure chevron)
  isGroup?: boolean;
  expanded?: boolean;
  onToggleExpand?: () => void;
  onUngroup?: () => void;
  itemCount?: number;
  // Edit mode reveals the per-row overflow (⋯) menu; operate mode hides all row actions.
  editMode?: boolean;
}

interface BorderStyle {
  borderColor: string;
  borderWidth: string;
  shadow: string;
}

interface StatusDisplay {
  text: string;
  color: string;
}

/**
 * TimeElementCard - A card component for displaying timeline elements
 *
 * Features:
 * - Left border colored by state (active=blue, expired=gray) or urgency (imminent/soon/moderate)
 * - NextUp elements get prominent styling (thicker border, shadow)
 * - Countdown display for upcoming elements
 * - Optional drag handle and edit/delete buttons
 */
const TimeElementCard: React.FC<TimeElementCardProps> = ({
  element,
  state,
  isNextUp = false,
  urgency,
  showCountdown = false,
  showActualTimes = false,
  formattedStartTime,
  currentTime,
  onEdit,
  onDelete,
  draggable = false,
  onDragStart,
  onDragEnd,
  onDragOver,
  isDragging = false,
  durationNote,
  offsetInfo,
  isGroup = false,
  expanded = false,
  onToggleExpand,
  onUngroup,
  itemCount,
  editMode = false,
}) => {
  // Determine border styling based on state and urgency
  // Uses URGENCY_COLORS from shared package for inline styles (more reliable than Tailwind classes)
  const getBorderStyle = (): BorderStyle => {
    if (state === 'expired') {
      return {
        borderColor: URGENCY_COLORS.EXPIRED,
        borderWidth: 'border-l-2',
        shadow: '',
      };
    }

    if (state === 'active') {
      return {
        borderColor: URGENCY_COLORS.ACTIVE,
        borderWidth: 'border-l-4',
        shadow: 'shadow-lg shadow-blue-500/20',
      };
    }

    // Upcoming elements - style based on urgency and nextUp status
    const urgencyColorMap: Record<UrgencyLevel, string> = {
      imminent: URGENCY_COLORS.IMMINENT,
      soon: URGENCY_COLORS.SOON,
      moderate: URGENCY_COLORS.MODERATE,
      distant: URGENCY_COLORS.DISTANT,
    };

    return {
      borderColor: urgency ? urgencyColorMap[urgency] : URGENCY_COLORS.MODERATE,
      borderWidth: isNextUp ? 'border-l-4' : 'border-l-2',
      shadow: isNextUp ? 'shadow-lg' : '',
    };
  };

  const borderStyle = getBorderStyle();

  // Get status text and color
  const getStatusDisplay = (): StatusDisplay | null => {
    if (state === 'active') {
      return { text: 'Happening Now', color: 'text-state-active' };
    }
    if (state === 'upcoming' && isNextUp) {
      // "Up Next" badge uses urgency color to match iOS
      const urgencyColorMap: Record<UrgencyLevel, string> = {
        imminent: 'text-urgency-imminent',
        soon: 'text-urgency-soon',
        moderate: 'text-urgency-moderate',
        distant: 'text-gray-500 dark:text-gray-400',
      };
      return { text: 'Up Next', color: urgency ? urgencyColorMap[urgency] : 'text-gray-500 dark:text-gray-400' };
    }
    return null;
  };

  const statusDisplay = getStatusDisplay();

  // Calculate progress for active elements
  const getActiveProgress = (): { progress: number; remaining: number } => {
    if (state !== 'active' || !currentTime) return { progress: 0, remaining: 0 };
    return {
      progress: calculateProgress(currentTime, element.startTime, element.duration),
      remaining: calculateTimeRemaining(currentTime, element.endTime)
    };
  };

  const activeProgress = getActiveProgress();

  // Countdown color based on state
  // NextUp: primary text color (prominent)
  // Others: urgency color matching left border
  const getCountdownColor = (): string => {
    if (isNextUp) return 'text-gray-900 dark:text-white';
    switch (urgency) {
      case 'imminent': return 'text-urgency-imminent';
      case 'soon': return 'text-urgency-soon';
      case 'moderate': return 'text-urgency-moderate';
      default: return 'text-gray-500 dark:text-gray-400';
    }
  };

  return (
    <div
      draggable={draggable}
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      onDragOver={onDragOver}
      className={`
        relative rounded-lg bg-white dark:bg-gray-800
        border border-gray-200 dark:border-gray-700
        ${borderStyle.borderWidth}
        ${borderStyle.shadow}
        ${draggable && !isDragging ? 'cursor-move' : ''}
        ${isDragging ? 'opacity-40' : ''}
        transition-all duration-200
        p-3 sm:p-4
      `}
      style={{ borderLeftColor: borderStyle.borderColor }}
    >
      <div className="flex items-center justify-between gap-3">
        {/* Left side: drag handle + content */}
        <div className="flex items-center gap-3 flex-1 min-w-0">
          {/* Drag handle */}
          {draggable && (
            <GripVertical className="w-5 h-5 text-gray-500 flex-shrink-0" />
          )}

          {/* Main content */}
          <div className="flex-1 min-w-0">
            {/* Element name and status — for a group, the disclosure chevron sits inline, just
                before the title (as if part of the name), so cards line up down the left edge. */}
            <div className="flex items-center gap-1.5 flex-wrap">
              {isGroup && onToggleExpand && (
                <button
                  onClick={(e) => { e.stopPropagation(); onToggleExpand(); }}
                  aria-label={expanded ? 'Collapse group' : 'Expand group'}
                  aria-expanded={expanded}
                  className="-ml-0.5 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 flex-shrink-0"
                >
                  <ChevronRight className={`w-4 h-4 transition-transform ${expanded ? 'rotate-90' : ''}`} />
                </button>
              )}
              <span className="text-gray-800 dark:text-gray-100 font-medium text-sm sm:text-base break-words">
                {element.description}
              </span>
              {showCountdown && statusDisplay && (
                <span className={`text-xs sm:text-sm ${statusDisplay.color}`}>
                  {statusDisplay.text}
                </span>
              )}
            </div>

            {/* Duration */}
            <div className="text-xs sm:text-sm text-gray-500 dark:text-gray-400 mt-1">
              Duration: <span className="font-mono font-medium">{formatDuration(element.duration)}</span>
              {durationNote && (
                <span className="ml-2 text-purple-500 dark:text-purple-400 text-xs">{durationNote}</span>
              )}
              {isGroup && typeof itemCount === 'number' && (
                <span className="ml-2 text-xs text-gray-400 dark:text-gray-500">· {itemCount} item{itemCount !== 1 ? 's' : ''}</span>
              )}
            </div>

            {/* Start time - actual times or example times */}
            {showActualTimes && formattedStartTime && (
              <div className="text-xs sm:text-sm text-gray-500 dark:text-gray-400">
                {state === 'upcoming' ? 'Starts at: ' : 'Started at: '}
                <span className="font-medium">{formattedStartTime}</span>
              </div>
            )}
            {!showActualTimes && formattedStartTime && (
              <div className="text-xs sm:text-sm text-gray-500 dark:text-gray-400 font-mono font-semibold">
                {formattedStartTime}
              </div>
            )}

            {/* Offset info (for fixed elements) */}
            {offsetInfo && (
              <div className="text-xs sm:text-sm text-gray-500 dark:text-gray-400">
                {offsetInfo}
              </div>
            )}

            {/* Countdown — on mobile it lives here (full width) instead of the cramped right gutter */}
            {showCountdown && state !== 'expired' && state !== 'active' && (
              <div className={`sm:hidden mt-1 text-base font-mono font-bold ${getCountdownColor()}`}>
                {utilityService.formatCountdown(element.countdown ?? 0)}
              </div>
            )}

            {/* Progress bar for active elements */}
            {state === 'active' && currentTime && (
              <div className="mt-2">
                {/* Progress bar track */}
                <div className="h-1.5 bg-gray-300 dark:bg-gray-700 rounded-full overflow-hidden">
                  {/* Progress fill */}
                  <div
                    className="h-full bg-state-active rounded-full transition-all duration-500 ease-linear"
                    style={{ width: `${activeProgress.progress * 100}%` }}
                  />
                </div>
                {/* Remaining time */}
                <div className="text-xs text-state-active mt-1">
                  {utilityService.formatCountdown(activeProgress.remaining)} remaining
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Right side: countdown + actions */}
        <div className="flex items-center gap-3 flex-shrink-0">
          {/* Countdown (desktop/tablet only — mobile renders it inside the content) */}
          {showCountdown && state !== 'expired' && state !== 'active' && (
            <span className={`hidden sm:inline text-xl md:text-2xl font-mono font-bold ${getCountdownColor()}`}>
              {utilityService.formatCountdown(element.countdown ?? 0)}
            </span>
          )}

          {/* Per-row overflow (⋯) menu — only in edit mode. Operate mode shows no row actions. */}
          {editMode && (() => {
            const actions: RowAction[] = isGroup
              ? [
                  onEdit && { label: 'Rename', onClick: onEdit, icon: <Edit3 className="w-4 h-4" /> },
                  onUngroup && { label: 'Ungroup', onClick: onUngroup, icon: <Ungroup className="w-4 h-4" /> },
                ].filter(Boolean) as RowAction[]
              : [
                  onEdit && { label: 'Edit', onClick: onEdit, icon: <Edit3 className="w-4 h-4" /> },
                  onDelete && { label: 'Delete', onClick: onDelete, icon: <Trash2 className="w-4 h-4" />, danger: true },
                ].filter(Boolean) as RowAction[];
            return <RowActionsMenu actions={actions} label={isGroup ? 'Group actions' : 'Element actions'} />;
          })()}
        </div>
      </div>
    </div>
  );
};

export default TimeElementCard;
